import React from 'react';
import { Link } from 'react-router-dom';
import { 
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { 
  Calendar, 
  Trophy, 
  Users, 
  BarChart3, 
  Building2,
  Target,
  UserCheck,
  Shield,
  Settings,
  Rss,
  Star
} from 'lucide-react';

interface RoleBasedNavigationMenuProps {
  userRole: string;
  permissions: {
    canCreateGames: boolean;
    canManageTeams: boolean;
    canManageVenues: boolean;
    isCoach: boolean;
    isTeamOwner: boolean;
  };
}

export const RoleBasedNavigationMenu: React.FC<RoleBasedNavigationMenuProps> = ({
  userRole,
  permissions
}) => {
  const getGameMenuItems = () => {
    const items = [
      { to: '/games', label: 'Browse All Games', icon: BarChart3 }
    ];

    // Only show Manage Games for users who can manage games
    if (permissions.canCreateGames || permissions.canManageTeams || permissions.canManageVenues) {
      items.push({ to: '/game-management', label: 'Manage Games', icon: Settings });
    }

    if (permissions.canCreateGames) {
      items.push({ to: '/create-game', label: 'Create Game', icon: Trophy });
    }

    // Remove scorekeeper items from Games menu - they belong in user profile menu

    // Remove Score Approvals from Games menu - it belongs in user profile menu

    return items;
  };

  const getExploreMenuItems = () => {
    const baseItems = [
      { to: '/teams-search', label: 'Find Teams', icon: Users },
      { to: '/highlights', label: 'Highlights', icon: Rss },
      { to: '/nominate', label: 'Nominate a Player', icon: Star },
      { to: '/my-nominations', label: 'My Nominations', icon: Star }
    ];

    // Parents shouldn't have access to teams - they should create a player account instead
    if (userRole === 'parent') {
      return baseItems.filter(item => item.to !== '/my-teams');
    }
    
    baseItems.push({ to: '/my-teams', label: 'My Teams', icon: Users });
    return baseItems;
  };

  const getCoachMenuItems = () => {
    const items = [];

    if (permissions.isCoach || permissions.isTeamOwner) {
      items.push(
        { to: '/dashboard', label: 'Dashboard', icon: Target }
      );
    }

    if (permissions.canManageTeams) {
      items.push(
        { to: '/team-players', label: 'Manage Players', icon: UserCheck }
      );
    }

    if (permissions.isCoach) {
      items.push(
        { to: '/team-coaches', label: 'Manage Coaches', icon: Shield },
        { to: '/player-stats', label: 'Player Stats', icon: BarChart3 }
      );
    }

    if (permissions.isCoach || permissions.isTeamOwner) {
      items.push(
        { to: '/analytics-dashboard', label: 'Analytics', icon: BarChart3 }
      );
    }

    return items;
  };

  const getVenueMenuItems = () => {
    const items = [];

    if (permissions.canManageVenues) {
      items.push(
        { to: '/venue-management', label: 'My Venues', icon: Building2 },
        { to: '/venue-bookings', label: 'Bookings', icon: Calendar },
        { to: '/venue-approvals', label: 'Game Approvals', icon: UserCheck }
      );
    }

    return items;
  };

  return (
    <NavigationMenu className="hidden md:flex" orientation="horizontal">
      <NavigationMenuList className="space-x-6">
        {/* Home */}
        <NavigationMenuItem>
          <Link
            to="/"
            className="text-gray-300 hover:text-sports-green transition-colors px-4 py-2"
          >
            Home
          </Link>
        </NavigationMenuItem>

        {/* BETA */}
        <NavigationMenuItem>
          <Link
            to="/agent-beta"
            className="bg-sports-green/20 text-sports-green border border-sports-green/30 px-3 py-1 rounded-full text-xs font-semibold hover:bg-sports-green/30 hover:border-sports-green/50 transition-colors"
          >
            BETA
          </Link>
        </NavigationMenuItem>

        {/* Games Menu */}
        <NavigationMenuItem>
          <NavigationMenuTrigger className="text-gray-300 hover:text-sports-green bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-sports-green">
            Games
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="w-56 p-2 bg-black border border-gray-800 shadow-lg">
              {getGameMenuItems().map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex items-center gap-3 px-3 py-2 text-gray-300 hover:text-sports-green hover:bg-gray-800 rounded transition-colors"
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              ))}
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>

        {/* Explore Menu */}
        <NavigationMenuItem>
          <NavigationMenuTrigger className="text-gray-300 hover:text-sports-green bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-sports-green">
            Explore
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="w-56 p-2 bg-black border border-gray-800 shadow-lg">
              {getExploreMenuItems().map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex items-center gap-3 px-3 py-2 text-gray-300 hover:text-sports-green hover:bg-gray-800 rounded transition-colors"
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              ))}
              {permissions.canManageVenues && getVenueMenuItems().map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex items-center gap-3 px-3 py-2 text-gray-300 hover:text-sports-green hover:bg-gray-800 rounded transition-colors"
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              ))}
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>


        {/* Shop */}
        <NavigationMenuItem>
          <Link 
            to="/shop" 
            className="text-gray-300 hover:text-sports-green transition-colors px-4 py-2 flex items-center"
          >
            Shop
            <span className="ml-2 bg-sports-green text-black text-xs px-2 py-1 rounded">Soon</span>
          </Link>
        </NavigationMenuItem>

        {/* Help */}
        <NavigationMenuItem>
          <NavigationMenuTrigger className="text-gray-300 hover:text-sports-green bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-sports-green">
            Help
          </NavigationMenuTrigger>
          <NavigationMenuContent className="left-auto right-0">
            <div className="w-56 p-2 bg-black border border-gray-800 shadow-lg">
              <Link
                to="/support"
                className="flex items-center gap-3 px-3 py-2 text-gray-300 hover:text-sports-green hover:bg-gray-800 rounded transition-colors"
              >
                Support
              </Link>
              <Link
                to="/faq"
                className="flex items-center gap-3 px-3 py-2 text-gray-300 hover:text-sports-green hover:bg-gray-800 rounded transition-colors"
              >
                FAQ
              </Link>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
};